import { Global, css } from '@emotion/react';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect } from 'react';

import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Oops from '~/common/components/Oops';
import Spinner from '~/common/components/Spinner';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { RULE_REFERENCE_TYPE_ID } from '~/common/constants';
import useGetLoanSummary, {
  type LoanSummary,
} from '~/common/hooks/useGetLoanSummary';
import Grid from '~/common/mui/Grids/Grid';
import { formatCurrency } from '~/common/util/format';
import ErrorBoundary from '~/pages/Home/ErrorBoundary';
import BreakdownView from '~/pages/IncomeCalculator/CalculationHistory/HistoricalBreakdown/CalculationBreakdown';
import { CalculationTypes } from '~/pages/IncomeCalculator/enums';
import useCalculation, {
  calculationKeys,
} from '~/pages/IncomeCalculator/queries/useCalculation';

import QualifyingIncomeWrapper from './QualifyingIncomeWrapper';
import { useGetNavigationDetails } from './hooks';

const qualifyingCalcs = new Set([
  CalculationTypes.QualifyingPaystub,
  CalculationTypes.QualifyingVoe,
  CalculationTypes.QualifyingYearEndPaystub,
]);

type PrintHeaderProps = {
  loanSummary?: LoanSummary;
};

function PrintHeader({ loanSummary }: Readonly<PrintHeaderProps>) {
  if (!loanSummary) {
    return null;
  }

  const borrowerName = loanSummary.borrower.fullName;

  return (
    <Grid
      css={css`
        background-color: white;
        padding: 0.25rem 2.5rem;
        border-bottom: 1px solid;
        grid-template-columns: repeat(2, 1fr);
        justify-content: space-between;
      `}
    >
      <Text as="h2" fontSize="sm">
        <b>Loan Number:</b> {loanSummary.loanInfo.loanNumber}
      </Text>
      <Text as="h2" fontSize="sm">
        <b>Borrower Name:</b> {borrowerName}
      </Text>
      <Text as="h2" fontSize="sm">
        <b>AUS Method: </b> {loanSummary.loanInfo.underwritingMethod}
      </Text>
      <Text as="h2" fontSize="sm">
        <b>Product Type: </b> {loanSummary.loanInfo.loanProductType}
      </Text>
    </Grid>
  );
}

export default function IncomeWorksheet() {
  return (
    <ErrorBoundary fallback={<Oops />}>
      <IncomeWorksheetPage />
    </ErrorBoundary>
  );
}

function IncomeWorksheetPage() {
  const { data, isFetching, isError, error } = useGetNavigationDetails();
  const { data: loanSummary, isLoading: fetchingLoanSummary } =
    useGetLoanSummary();
  const queryClient = useQueryClient();
  const fetchingCalcs = queryClient.isFetching({
    queryKey: calculationKeys.all,
  });
  const isLoading = isFetching || fetchingLoanSummary || fetchingCalcs > 0;

  useEffect(() => {
    if (
      isLoading ||
      queryClient.getQueriesData({ queryKey: calculationKeys.all }).length <
        data.length
    ) {
      return;
    }

    // eslint-disable-next-line no-console
    const timeout = setTimeout(() => console.log('Take my picture 📸'), 1000);
    return () => clearTimeout(timeout);
  }, [isLoading, data?.length, queryClient]);

  if (isFetching || fetchingLoanSummary) {
    return <Spinner loadingMessage="Loading, please wait" />;
  }

  if (isError) {
    throw new Error('Could not get list of relevant incomes: ' + error);
  }

  return (
    <>
      <Global
        styles={css`
          #boltHeader,
          footer {
            display: none;
          }
          * {
            line-height: 1;
          }
          h1 {
            font-size: 1.5rem;
          }
          h2 {
            font-size: 1.25rem;
          }
          h3 {
            font-size: 0.925rem;
          }
          h4 {
            font-size: 0.875rem;
          }
          p,
          dt,
          dd,
          td {
            font-size: 0.825rem;
          }
        `}
      />
      <PrintHeader loanSummary={loanSummary} />
      <Text as="h1" mx="2rem" mt="1rem">
        BOLT Income Worksheet
      </Text>
      <Flex m="2rem" mt="1rem" flexDirection="column" gap="1rem">
        {!data.length && (
          <Text>This loan does not have any valid income sources.</Text>
        )}
        {data?.map((gutterItem) => {
          const isEmployment =
            gutterItem.ruleReferenceTypeId ==
            RULE_REFERENCE_TYPE_ID.EMPLOYER_ID;
          const isRental =
            gutterItem.ruleReferenceTypeId == RULE_REFERENCE_TYPE_ID.ADDRESS_ID;

          return (
            <CalculationBreakdown
              key={gutterItem.gutterItemId}
              incomeIdentifier={
                isEmployment
                  ? {
                      obfuscatedLenderDatabaseId:
                        gutterItem.customerLenderDatabaseId,
                      customerRecordId: gutterItem.customerRecordId,
                      employerId: gutterItem.ruleReferenceId,
                    }
                  : undefined
              }
              rentalAddressId={
                isRental ? gutterItem.ruleReferenceId : undefined
              }
              selectedCalculationId={
                +gutterItem.additionalReferences.CalculationId
              }
              incomeTitle={gutterItem.parentTitle}
            />
          );
        })}
      </Flex>
    </>
  );
}

type CalculationBreakdownProps = {
  incomeIdentifier?: {
    obfuscatedLenderDatabaseId: number;
    customerRecordId: number;
    employerId: number;
  };
  rentalAddressId?: number;
  incomeTitle: string;
  selectedCalculationId?: number;
};

function CalculationBreakdown({
  incomeIdentifier,
  rentalAddressId,
  incomeTitle,
  selectedCalculationId,
}: Readonly<CalculationBreakdownProps>) {
  const { data, isFetching, isError, error } = useCalculation(
    null,
    null,
    incomeIdentifier,
    rentalAddressId ? { addressLdId: 1, addressId: rentalAddressId } : null,
  );

  if (!isFetching && isError) {
    throw new Error(
      `Could not get calculation data: CRID=${incomeIdentifier?.customerRecordId} EID=${incomeIdentifier?.employerId} AID=${rentalAddressId}. Error: ${error}`,
    );
  }

  if (isFetching || !data) {
    return <Spinner loadingMessage="Loading the calculation" />;
  }

  let selectedResult =
    data.results.filter(
      (result) =>
        result.isSelected || result.calculationId === selectedCalculationId,
    ) ?? [];

  if (!selectedResult.length && !!rentalAddressId) {
    selectedResult = data.results
      .toSorted((a, b) => b.calculationId - a.calculationId)
      .slice(0, 1);
  }

  const incomeType = selectedResult[0]?.incomeCalculatorType;

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      bgColor="white"
      role="article"
      css={css`
        break-inside: avoid;
      `}
    >
      <Box role="heading" ml="0.875rem" my="1rem">
        <Text as="h2">{incomeTitle}</Text>
        {incomeType !== 'Miscellaneous' && <Text as="h3">{incomeType}</Text>}
      </Box>
      <Stack spacing={4} direction="column">
        {selectedResult.map((result) => (
          <SingleBreakdown
            results={selectedResult}
            result={result}
            incomeIdentifier={incomeIdentifier}
            key={result.calculationId}
          />
        ))}
      </Stack>
      <CommentBox comments={data.comment} />

      {!selectedResult.length && <Text p="1rem">No selected calculation</Text>}
    </Box>
  );
}

type SingleBreakdownProps = {
  results: any[];
  result: any;
  incomeIdentifier?: {
    obfuscatedLenderDatabaseId?: number;
    customerRecordId?: number;
    employerId?: number;
  };
};

function SingleBreakdown({
  results,
  result,
  incomeIdentifier,
}: Readonly<SingleBreakdownProps>) {
  if (qualifyingCalcs.has(result.calculatorTypeId)) {
    // This should be moved to the calculation breakdown component.
    return (
      <>
        <QualifyingIncomeWrapper incomeIdentifier={incomeIdentifier} />
        <IncomeOverrides overrideAmounts={result.overrideAmounts} />
      </>
    );
  }

  return (
    <>
      <BreakdownView
        onNewWindow={() => {}}
        results={results}
        selectedResult={result}
        hideAccordion
      />
      <IncomeOverrides overrideAmounts={result.overrideAmounts} />
    </>
  );
}

type IncomeOverridesProps = {
  overrideAmounts: {
    overriddenBaseIncome?: number;
    overriddenBonusIncome?: number;
    overriddenCommissionIncome?: number;
    overriddenNetRentalIncome?: number;
    overriddenOtherIncome?: number;
    overriddenOvertimeIncome?: number;
    overriddenSeasonalIncome?: number;
    overriddenTipIncome?: number;
  };
};

function IncomeOverrides({ overrideAmounts }: Readonly<IncomeOverridesProps>) {
  if (Object.values(overrideAmounts).every((amount) => !amount)) {
    return null;
  }

  const entries = Object.entries(overrideAmounts).filter((e) => !!e[1]);

  return (
    <Grid
      gridTemplateColumns="repeat(2, max-content)"
      margin="0 24px 12px 24px"
      rowGap="0.5rem"
      fontSize="0.875rem"
    >
      {entries.map(([key, amount]) => {
        const type = /overridden(\w+)Income/.exec(key)[1];

        return (
          <Fragment key={key}>
            <Text as="span" fontWeight="bold">
              Manually Updated {type === 'NetRental' ? 'Net Rental' : type}{' '}
              Income: &nbsp;
            </Text>

            <Text justifySelf="end">{formatCurrency(amount)}</Text>
          </Fragment>
        );
      })}
    </Grid>
  );
}

type CommentBoxProps = {
  comments?: string;
};

function CommentBox({ comments }: Readonly<CommentBoxProps>) {
  if (!comments) {
    return null;
  }

  return (
    <Box
      margin="0 24px 12px 24px"
      paddingTop="12px"
      borderTop="1px solid"
      borderColor="gray.200"
    >
      <Text marginBottom="0.5rem" fontWeight="bold" fontSize="0.875rem">
        Comments:
      </Text>
      <Text fontSize="0.875rem">{comments}</Text>
    </Box>
  );
}
